/**
 * Task din cererea de tipografie.
 *
 * Alexandru, 01.10.2026: „aici ar fi nevoie să poată fi generat task din fiecare cerere".
 *
 * Nu creează task-ul dintr-un clic, cu toate câmpurile ghicite: deschide un formular
 * PRE-COMPLETAT, în care singurul lucru de ales e DEPARTAMENTUL (12.10.2026: task-urile sunt pe
 * departamente — ajunge la „De preluat" acolo și îl ia cine are timp; nu se alege un om). Restul —
 * titlul, specificațiile, termenul — vin din cerere, pentru că sunt deja scrise acolo.
 *
 * Task-ul se leagă de cerere prin `refType`/`refId`, mecanismul folosit deja de alerte, campanii
 * și mesajele din inbox. De aceea butonul devine LINK când există deja unul: altfel se fac trei
 * task-uri pentru aceeași cerere, fiindcă nimeni nu ține minte dacă a apăsat.
 */
import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { arataDublura } from '../../stores/dublura'
import { departamentulRolului, numeDepartament, type DepartamentSlug } from '@b4l/shared'
import { AlegeDepartamente, listaDepartamente, trimiteLa } from '../taskuri/AlegeDepartamente'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { Modal } from '../ui/Modal'
import { Button } from '../ui/Button'
import { Field } from '../ui/Field'
import { useAuthStore } from '../../stores/auth'

export interface CerereDeTask {
  id: number
  b4lId: string
  materialType: string
  materialDetails: string | null
  quantity: number
  deadline: string | null
  sourceDetails: string | null
}

/** Data, în forma pe care o cere `<input type="date">`. */
function ziua(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10)
}

export function ModalTaskCerere({
  cerere,
  onClose,
  onCreat,
}: {
  cerere: CerereDeTask
  onClose: () => void
  onCreat: () => void
}) {
  const qc = useQueryClient()
  const [titlu, setTitlu] = useState(`${cerere.b4lId} — ${cerere.materialType}`)
  const [descriere, setDescriere] = useState(
    [
      cerere.materialDetails,
      cerere.sourceDetails ? `Sursa: ${cerere.sourceDetails}` : null,
      `Cantitate: ${cerere.quantity}`,
    ]
      .filter(Boolean)
      .join('\n'),
  )
  /* Termenul cererii E termenul task-ului. Daca ar fi gol, s-ar pune altul, din cap. */
  const [termen, setTermen] = useState(ziua(cerere.deadline))
  const [urgent, setUrgent] = useState(false)
  const user = useAuthStore((s) => s.user)
  // Cererile de tipografie sunt treaba Marketingului; cine e in alt departament poate alege altul.
  const [alese, setAlese] = useState<DepartamentSlug[]>([
    (user && departamentulRolului(user.role)) ?? 'marketing',
  ])

  const creeaza = useMutation({
    mutationFn: async () =>
      (
        await api.post('/tasks', {
          departamente: alese,
          titlu: titlu.trim(),
          descriere: descriere.trim() || null,
          termen: termen || null,
          urgent,
          refType: 'cerere_print',
          refId: cerere.id,
        })
      ).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['print-requests'] })
      void qc.invalidateQueries({ queryKey: ['taskuri'] })
      toast.success(`Task trimis la ${listaDepartamente(alese)}, la „De preluat”.`)
      onCreat()
      onClose()
    },
    onError: (e: unknown) => {
      if (arataDublura(e, [titlu, descriere].filter((x) => x.trim()).join('\n\n'))) return
      const err = e as { response?: { data?: { error?: { message?: string } } } }
      toast.error(err?.response?.data?.error?.message ?? 'Task-ul nu a putut fi creat.')
    },
  })

  return (
    <Modal
      open
      onClose={onClose}
      title={`Task din ${cerere.b4lId}`}
      inchideLaClicInAfara={false}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Renunță
          </Button>
          <Button
            disabled={!titlu.trim() || !alese.length}
            loading={creeaza.isPending}
            onClick={() => creeaza.mutate()}
          >
            {trimiteLa(alese)}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Titlu" value={titlu} onChange={(e) => setTitlu(e.target.value)} />

        <div>
          <label className="mb-1 block text-xs font-medium text-b4l-text-muted">Descriere</label>
          <textarea
            value={descriere}
            onChange={(e) => setDescriere(e.target.value)}
            rows={4}
            className="w-full rounded border border-b4l-border px-3 py-2 text-sm"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field
            label="Termen"
            type="date"
            value={termen}
            onChange={(e) => setTermen(e.target.value)}
          />
          <label className="flex items-end gap-2 pb-2 text-sm">
            <input type="checkbox" checked={urgent} onChange={(e) => setUrgent(e.target.checked)} />
            Urgent
          </label>
        </div>

        <div className="grid gap-1.5">
          <span className="text-xs font-medium text-b4l-text-muted">
            {user?.role === 'admin' ? 'Pentru ce departamente?' : 'Pentru ce departament?'}
          </span>
          <AlegeDepartamente alese={alese} setAlese={setAlese} />
          <span className="text-mic text-b4l-text-dim">
            {alese.length === 0
              ? 'Alege departamentul.'
              : alese.length > 1
                ? `Pleacă câte un task la ${listaDepartamente(alese)}, în „De preluat”. Fiecare îl preia și îl închide separat.`
                : `Intră la „De preluat” în ${numeDepartament(alese[0]!)}. Îl ia cine are timp, cu „Preia”.`}
          </span>
        </div>
      </div>
    </Modal>
  )
}
